import { useState, useEffect, useCallback, useRef } from "react";
import SubHeader from "components/common/SubHeader";
import { getUserLikedPosts, togglePostLike } from "api/community";
import useUserStore from "@store/useUserStore";
import MediaCarousel from "components/community/MediaCarousel";
import useCommunityCategories from "hooks/useCommunityCategories";
import { formatTime } from "utils/util";
import { stripMentionsToText } from "utils/mention";
import NoData from "components/common/NoData";
import "pages/community/community.scss";

const LikedPosts = () => {
    const { categoryMap } = useCommunityCategories();
    const userInfo = useUserStore((state) => state.userInfo);
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const bottomRef = useRef(null);

    const loadPosts = useCallback(
        async (pageNum, append = false) => {
            if (!userInfo) return;
            try {
                if (append) {
                    setIsLoadingMore(true);
                } else {
                    setIsLoading(true);
                    setPosts([]);
                }
                const result = await getUserLikedPosts(userInfo.user_id, pageNum);
                if (result.success) {
                    setPosts((prev) =>
                        append ? [...prev, ...result.data] : result.data
                    );
                    setTotalPages(result.meta?.totalPages || 0);
                }
            } catch (error) {
                console.error("좋아요 게시글 로딩 실패:", error);
            } finally {
                setIsLoading(false);
                setIsLoadingMore(false);
            }
        },
        [userInfo]
    );

    useEffect(() => {
        setPage(1);
        loadPosts(1);
    }, [loadPosts]);

    useEffect(() => {
        if (!bottomRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !isLoadingMore && page < totalPages) {
                    const nextPage = page + 1;
                    setPage(nextPage);
                    loadPosts(nextPage, true);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(bottomRef.current);
        return () => observer.disconnect();
    }, [page, totalPages, isLoadingMore, loadPosts]);

    const handleLikeToggle = useCallback(
        async (e, postId) => {
            e.stopPropagation();
            setPosts((prev) =>
                prev.map((p) =>
                    p.id === postId
                        ? {
                              ...p,
                              is_liked: !p.is_liked,
                              like_count: p.is_liked ? p.like_count - 1 : p.like_count + 1,
                          }
                        : p
                )
            );
            try {
                await togglePostLike(postId);
            } catch {
                setPosts((prev) =>
                    prev.map((p) =>
                        p.id === postId
                            ? {
                                  ...p,
                                  is_liked: !p.is_liked,
                                  like_count: p.is_liked ? p.like_count - 1 : p.like_count + 1,
                              }
                            : p
                    )
                );
            }
        },
        []
    );

    return (
        <div className="community-page community-list">
            <SubHeader title="좋아요 한 글" />
            <div>
                {isLoading ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                        불러오는 중...
                    </div>
                ) : posts.length === 0 ? (
                    <NoData
                        title=""
                        desc="아직 좋아요한 게시글이 없어요."
                    />
                ) : (
                    <div className="post-list">
                            {posts.map((post) => (
                                <article
                                    key={post.id}
                                    className="post-card"
                                    onClick={() => window.open(`/community/${post.id}`, "_blank")}
                                >
                                    <div className="author-row">
                                        {post.author_profile_image ? (
                                            <img src={post.author_profile_image} alt="" className="author-avatar" />
                                        ) : (
                                            <div className="author-avatar-placeholder">
                                                {(post.author_nickname || post.author_name || "?").charAt(0)}
                                            </div>
                                        )}
                                        <div className="author-info">
                                            <div className="author-name-row">
                                                <span className="author-nickname">
                                                    {post.author_nickname || post.author_name}
                                                </span>
                                                {post.post_category && (
                                                    <>
                                                        <span className="separator">›</span>
                                                        <span className="activity-type">
                                                            {categoryMap[post.post_category] || post.post_category}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                            <span className="post-time">{formatTime(post.created_at)}</span>
                                        </div>
                                    </div>

                                    <div className="post-content">{stripMentionsToText(post.content)}</div>

                                    <MediaCarousel mediaUrls={post.media_urls} />

                                    <div className="action-bar">
                                        <button className="action-item" onClick={(e) => handleLikeToggle(e, post.id)}>
                                            <svg className="action-icon" viewBox="0 0 20 20" fill="none">
                                                <path
                                                    d="M10 17.5s-7-4.5-7-9.5A4 4 0 0 1 10 5a4 4 0 0 1 7 3c0 5-7 9.5-7 9.5z"
                                                    fill={post.is_liked ? "#e40000" : "none"}
                                                    stroke={post.is_liked ? "#e40000" : "#464a4d"}
                                                    strokeWidth="1.5"
                                                />
                                            </svg>
                                            {post.like_count > 0 && <span className="action-count">{post.like_count}</span>}
                                        </button>
                                        <button
                                            className="action-item"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(`/community/${post.id}`, "_blank");
                                            }}
                                        >
                                            <svg className="action-icon" viewBox="0 0 20 20" fill="none">
                                                <path
                                                    d="M17 10c0 3.866-3.134 7-7 7a7.2 7.2 0 0 1-3.5-.9L3 17l.9-3.5A7.2 7.2 0 0 1 3 10c0-3.866 3.134-7 7-7s7 3.134 7 7z"
                                                    stroke="#464a4d"
                                                    strokeWidth="1.5"
                                                    fill="none"
                                                />
                                            </svg>
                                            {post.comment_count > 0 && <span className="action-count">{post.comment_count}</span>}
                                        </button>
                                    </div>
                                </article>
                            ))}
                            <div ref={bottomRef} />
                        {isLoadingMore && <div className="loading-more">로딩 중...</div>}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LikedPosts;
